import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cartService from '../api/services/cart.service';
import { useSession } from './SessionContext'; // Import useSession
import { getProductImages } from '../utils/imageUtils';
import { useToast } from '../admin/context/ToastContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { userUuid, ipAddress } = useSession(); // Get both session IDs
  const { showToast } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasFetchedCart, setHasFetchedCart] = useState(false); // true after first real cart fetch completes
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const getStoredGuestIdentifier = () => {
    const cachedIp = localStorage.getItem('chill_tree_guest_ip');
    return cachedIp || '';
  };

  const fetchCart = useCallback(async (overrides = {}) => {
    const effectiveUserUuid = overrides.user_uuid ?? userUuid;
    const effectiveIpAddress = overrides.ip_address ?? ipAddress;
    setLoading(true);
    try {
      let res;
      if (effectiveUserUuid) {
        res = await cartService.list(
          { user_uuid: effectiveUserUuid },
          { skipAuth: false, cacheBust: true }
        );
      } else if (effectiveIpAddress) {
        res = await cartService.list(
          { ip_address: effectiveIpAddress },
          { skipAuth: true, cacheBust: true }
        );
      } else {
        setLoading(false);
        return;
      }
      const data = res?.data || res;
      const items = data?.items || [];
      const total_price = data?.total_price || 0;
      setCartTotal(total_price);

      const mappedItems = items.map(item => {
        const productDetails = item.product_details || {};
        const variantDetails = item.variant_details || {};
        const images = getProductImages(productDetails);
        const effectivePrice = Number(item.amount || item.subscription_price || item.variant_price || 0);
        const originalPrice = Number(item.variant_price || 0);
        const totalAmount = Number(item.total_amount || 0);

        return {
          id: item.uuid || item._id,
          productId: item.item_uuid,
          name: productDetails.name || 'Product',
          price: effectivePrice,
          originalPrice: originalPrice,
          totalAmount: totalAmount,
          quantity: item.quantity,
          image: images.length > 0 ? images[0] : '',
          backendCartUuid: item.uuid || item._id,
          purchaseType: item.purchase_type,
          selectedVariant: variantDetails,
          billingDuration: item.billing_duration,
          discountPercentage: variantDetails.discount_percentage || 0,
          multiplier: item.multiplier || 1
        };
      });
      setCartItems(mappedItems);
      setHasFetchedCart(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setCartItems([]);
        setCartTotal(0);
        setHasFetchedCart(true);
      } else {
        console.error("Failed to fetch cart:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [userUuid, ipAddress]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (product) => {
    // Validate payload (base fields)
    if (!product || !product.item_uuid) {
      showToast('Product information is missing', 'error');
      throw new Error('Product information is missing');
    }

    if (!product.variant_uuid) {
      showToast('Please select a product variant', 'error');
      throw new Error('Variant is required');
    }

    // Always attach session identifiers here so every caller (ProductDetails, ProductCard, etc.)
    // behaves consistently.
    const sessionIdentifier = userUuid || ipAddress;
    if (!sessionIdentifier) {
      showToast('Please refresh the page to continue', 'error');
      throw new Error('Session identifier is missing');
    }

    const payload = {
      ...product,
      // Prefer logged-in user UUID if available, else guest IP address.
      ...(userUuid ? { user_uuid: userUuid } : {}),
      ...(!userUuid && ipAddress ? { ip_address: ipAddress } : {}),
    };

    try {
      await cartService.create(payload);
      await fetchCart(); // Refresh cart from backend
      showToast('Item added to cart successfully!', 'success');
    } catch (err) {
      console.error("Failed to add item to cart:", err);
      
      // Extract error message from response
      let errorMessage = 'Failed to add item to cart. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showToast(errorMessage, 'error');
      throw err; // Re-throw to allow UI to handle it
    }
  };

  const removeFromCart = async (cartUuid, silent = false, skipRefresh = false) => {
    try {
      await cartService.delete(cartUuid);
      setCartItems(prevItems => prevItems.filter(item => item.id !== cartUuid));
      if (!silent) {
        showToast('Item removed from cart successfully!', 'success');
      }
      if (!skipRefresh) {
        // Refresh cart to update total only when removal is final.
        await fetchCart();
      }
    } catch (err) {
      console.error("Failed to remove item:", err);
      const errorMessage = err.response?.data?.message || 'Failed to remove item. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  const updateQuantity = async (cartUuid, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartUuid);
      return;
    }
    try {
      await cartService.updateQuantity(cartUuid, quantity);
      await fetchCart(); // Refresh cart to sync total price and individual item totals
      showToast('Cart updated successfully!', 'success');
    } catch (err) {
      console.error("Failed to update quantity:", err);
      const errorMessage = err.response?.data?.message || 'Failed to update quantity. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartTotal;
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const isProductInCart = (productId, variantUuid = null) => {
    return cartItems.some(item => {
      if (item.productId !== productId) return false;
      if (variantUuid) {
        const itemVariantUuid = item.selectedVariant?.variant_uuid || item.selectedVariant?.uuid;
        return itemVariantUuid === variantUuid;
      }
      return true;
    });
  };

  const getCartItemForProduct = (productId, variantUuid = null) => {
    return cartItems.find(item => {
      if (item.productId !== productId) return false;
      if (variantUuid) {
        const itemVariantUuid = item.selectedVariant?.variant_uuid || item.selectedVariant?.uuid;
        return itemVariantUuid === variantUuid;
      }
      return true;
    });
  };

  // Upgrade guest cart (identified by ipAddress) to logged-in user cart (userUuid)
  const upgradeGuestCartToUser = async (override = {}, options = {}) => {
    const effectiveUserUuid = override.user_uuid ?? userUuid;
    const effectiveIpAddress = override.ip_address ?? ipAddress ?? getStoredGuestIdentifier();
    const silent = !!options.silent;

    if (!effectiveUserUuid || !effectiveIpAddress) {
      // Nothing to upgrade: either already user-only cart or no guest identifier
      return false;
    }

    try {
      await cartService.updateUserCart({
        user_uuid: effectiveUserUuid,
        ip_address: effectiveIpAddress,
      });
      // After successful upgrade, refetch cart using userUuid
      await fetchCart({ user_uuid: effectiveUserUuid });
      if (!silent) {
        showToast('Your cart has been linked to your account.', 'success');
      }
      return true;
    } catch (err) {
      console.error('Failed to upgrade guest cart to user:', err);
      // Fallback migration: read guest cart and recreate for logged-in user.
      try {
        const guestRes = await cartService.list(
          { ip_address: effectiveIpAddress },
          { skipAuth: true, cacheBust: true }
        );
        const guestData = guestRes?.data || guestRes;
        const guestItems = Array.isArray(guestData?.items) ? guestData.items : [];

        for (const item of guestItems) {
          const fallbackPayload = {
            user_uuid: effectiveUserUuid,
            item_uuid: item.item_uuid || item.product_uuid || item.item_details?.uuid,
            variant_uuid:
              item.variant_uuid ||
              item.variant_details?.variant_uuid ||
              item.variant_details?.uuid,
            quantity: Number(item.quantity || 1),
            purchase_type: item.purchase_type || 'one_time',
          };

          if (item.billing_duration) {
            fallbackPayload.billing_duration = Number(item.billing_duration);
          }

          if (fallbackPayload.item_uuid && fallbackPayload.variant_uuid) {
            // Best-effort migration; ignore per-item duplicate errors.
            try {
              await cartService.create(fallbackPayload);
            } catch (createErr) {
              console.warn('Skipping guest cart item migration:', createErr);
            }
          }
        }

        await fetchCart({ user_uuid: effectiveUserUuid });
        if (!silent) {
          showToast('Your cart has been linked to your account.', 'success');
        }
        return true;
      } catch (fallbackErr) {
        console.error('Guest cart fallback migration failed:', fallbackErr);
        const errorMessage =
          err.response?.data?.message ||
          fallbackErr.response?.data?.message ||
          'Could not link your guest cart to your account.';
        if (!silent) {
          showToast(errorMessage, 'error');
        }
        return false;
      }
    }
  };

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const value = {
    cartItems,
    cartTotal,
    loading,
    hasFetchedCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    fetchCart,
    isSidebarOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    isProductInCart,
    getCartItemForProduct,
    upgradeGuestCartToUser,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

